/**
 * ConnectionStatusDot — Integration Hub
 * -----------------------------------------------------------------------------
 * Indicador discreto de situação de uma conexão: bolinha colorida + rótulo em
 * cor de texto padrão (foreground). Substitui os badges coloridos, que
 * poluíam visualmente a listagem e o modal de aplicativos.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-24 v1.0.0 - Criação inicial (bolinha + texto preto).
 */

import { cn } from '@/lib/utils';

/** Aparência (rótulo + cor da bolinha) de cada status devolvido pela RPC */
export const CONNECTION_STATUS_META: Record<string, { label: string; dot: string }> = {
  // Backend grava 'active' após teste bem-sucedido; 'connected' mantido por compatibilidade
  active: { label: 'Conectada', dot: 'bg-emerald-500' },
  connected: { label: 'Conectada', dot: 'bg-emerald-500' },
  pending: { label: 'Não testada', dot: 'bg-muted-foreground' },
  error: { label: 'Com erro', dot: 'bg-destructive' },
  disabled: { label: 'Inativa', dot: 'bg-muted-foreground/50' },
};

interface ConnectionStatusDotProps {
  /** Status cru da conexão (active | connected | pending | error | disabled) */
  status?: string;
  /** Rótulo customizado (ex.: "Conectado" / "Não conectado" no cabeçalho) */
  label?: string;
  /** Classe extra da bolinha, quando o status não for usado */
  dotClassName?: string;
  className?: string;
}

/** Bolinha de status + texto em cor preta/foreground */
export function ConnectionStatusDot({
  status,
  label,
  dotClassName,
  className,
}: ConnectionStatusDotProps) {
  const meta = (status && CONNECTION_STATUS_META[status]) || CONNECTION_STATUS_META.pending;

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-foreground', className)}>
      <span className={cn('h-2 w-2 shrink-0 rounded-full', dotClassName ?? meta.dot)} />
      {label ?? meta.label}
    </span>
  );
}
